// src/shared/components/ui/MinimalisticTable.tsx
import React from 'react';
import classNames from 'classnames';
import SearchBox from '../ui/SearchBox';

export interface ColumnDef<RowType> {
  header: React.ReactNode;
  accessor: keyof RowType | '';
  Cell?: (value: any, row: RowType) => React.ReactNode;
}

export interface MinimalisticTableProps<RowType> {
  columns: ColumnDef<RowType>[];
  data: RowType[];
  noDataMessage?: string;

  showNewButton?: boolean;
  onNew?: () => void;

  editableAccessors?: Array<keyof RowType>;
  newRowValues?: Partial<Record<keyof RowType, any>>;
  onNewRowChange?: (accessor: keyof RowType, val: any) => void;
  onAcceptNew?: () => void;
  onCancelNew?: () => void;

  showSearch?: boolean;
  searchPlaceholder?: string;
  /** Called when user presses Enter in the search box */
  onSearchEnter?: () => void;

  /** Fired when a non-new row is clicked */
  onRowClick?: (row: RowType) => void;
  /** Return true if that row should respond to clicks */
  isRowClickable?: (row: RowType) => boolean;
}

export default function MinimalisticTable<RowType>({
  columns,
  data,
  noDataMessage = 'No data found.',
  showNewButton = true,
  onNew,
  editableAccessors,
  newRowValues,
  onNewRowChange,
  onAcceptNew,
  onCancelNew,

  showSearch = true,
  searchPlaceholder = 'Search…',
  onSearchEnter,

  onRowClick,
  isRowClickable,
}: MinimalisticTableProps<RowType>) {
  // ─── Search State ────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  // ─── Filtered Data ────────────────────────────────────────────────────
  const filteredData: RowType[] = React.useMemo(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        if (!col.accessor) return false;
        const cellValue = (row[col.accessor] as any);
        if (cellValue == null) return false;
        return String(cellValue).toLowerCase().includes(lower);
      })
    );
  }, [data, searchTerm, columns]);

  return (
    <table className="w-full text-left border-collapse">
      <thead>
        <tr>
          {columns.map((col, idx) => {
            const isLast = idx === columns.length - 1;
            const shouldShowButton = isLast && showNewButton && !!onNew;

            return (
              <th className="px-4 pt-2 pb-0 border-b border-neutral-700" key={idx}>
                {isLast ? (
                  <div className="flex items-center justify-between">
                    <span>{col.header}</span>
                    <div className="flex items-center space-x-2">
                      {showSearch && (
                        <div className="w-48">
                          <SearchBox
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder={searchPlaceholder}
                            onEnter={onSearchEnter}
                          />
                        </div>
                      )}
                      {shouldShowButton && (
                        <button
                          onClick={onNew}
                          className="bg-transparent text-emerald-500 px-2 py-1 hover:text-emerald-400"
                        >
                          New
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-end h-full justify-start">
                    <span>{col.header}</span>
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {filteredData.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-4 py-2 text-center text-neutral-500"
            >
              {noDataMessage}
            </td>
          </tr>
        ) : (
          filteredData.map((row, rowIdx) => {
            const isNewRow = (row as any).__isNew === true;
            // determine clickability
            const clickable =
              Boolean(onRowClick) &&
              !isNewRow &&
              (isRowClickable ? isRowClickable(row) : true);
            const trClasses = classNames(
              'border-b border-neutral-800 even:bg-neutral-900',
              { 'hover:bg-neutral-950 cursor-pointer': clickable }
            );

            return (
              <tr
                key={rowIdx}
                className={trClasses}
                onClick={() => clickable && onRowClick!(row)}
              >
                {columns.map((col, colIdx) => {
                  const rawValue = col.accessor
                    ? (row[col.accessor] as any)
                    : undefined;

                  // A) NEW‐ROW placeholder logic
                  if (isNewRow) {
                    if (
                      editableAccessors &&
                      col.accessor &&
                      editableAccessors.includes(col.accessor)
                    ) {
                      return (
                        <td key={colIdx} className="px-4 py-2 align-top">
                          <input
                            type="text"
                            value={newRowValues?.[col.accessor] ?? ''}
                            onChange={(e) =>
                              onNewRowChange?.(
                                col.accessor as any,
                                e.target.value
                              )
                            }
                            className="w-full bg-neutral-800 text-neutral-100 px-2 py-1 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      );
                    }
                    if (colIdx === columns.length - 1) {
                      return (
                        <td key={colIdx} className="px-4 py-2 align-top">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAcceptNew?.();
                              }}
                              className="text-emerald-500 hover:text-emerald-400"
                              disabled={!onNewRowChange}
                            >
                              Accept
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onCancelNew?.();
                              }}
                              className="text-rose-500 hover:text-rose-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      );
                    }
                    return (
                      <td key={colIdx} className="px-4 py-2 align-top">
                        —
                      </td>
                    );
                  }

                  // B) NORMAL ROW: custom Cell or rawValue
                  return (
                    <td key={colIdx} className="px-4 py-2 align-top">
                      {col.Cell ? col.Cell(rawValue, row) : rawValue}
                    </td>
                  );
                })}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}
